/* A PERSON'S OWN MENU ORDER (Jay Jung, 2026-10-21: "make its menu order customized by users (drag & drop)").

   The sidebar is still built from the grants, in its default order (Header.jsx); this only re-orders what is already
   there. Stored per account as { folders: [folderId…], items: { folderId: [entryKey…] } } (00_PORTAL_user_menu_order,
   sent with the sign-in answer). Entries move inside their own folder only — the folders are what the portal's
   screens are filed under, and a Courier entry dragged into Finance would be a menu nobody else can talk about. */

// What an entry is called in the stored order: a merged module by its own name (its first screen depends on the
// reader's grants), anything else by where it goes.
export const menuKeyOf = (item) => item.menuKey || item.path || item.external || item.label;

/* Put `list` in the order `saved` names. What `saved` does not name — an entry added to the portal after the person
   arranged their menu, or one they could not see then — keeps its place beside the entry it follows by default,
   rather than being pushed to the end where nobody looks for it. */
export const arrange = (list, saved, keyOf = (x) => x) => {
    if (!Array.isArray(saved) || !saved.length) return list;
    const rank = new Map(saved.map((k, i) => [k, i]));
    const out = list.filter((x) => rank.has(keyOf(x))).sort((a, b) => rank.get(keyOf(a)) - rank.get(keyOf(b)));
    list.forEach((x, i) => {
        if (rank.has(keyOf(x))) return;
        let at = 0;
        for (let j = i - 1; j >= 0; j -= 1) {
            const p = out.indexOf(list[j]);
            if (p >= 0) { at = p + 1; break; }
        }
        out.splice(at, 0, x);
    });
    return out;
};

// The folders, and the entries inside each, in the person's order.
export const applyMenuOrder = (folders, order) => {
    if (!order) return folders;
    const items = order.items || {};
    return arrange(folders, order.folders, (f) => f.id)
        .map((f) => (items[f.id] ? { ...f, items: arrange(f.items, items[f.id], menuKeyOf) } : f));
};

/* What to store after the person arranged what they can SEE. A folder or entry hidden under the role they are acting
   as now keeps its place from the order saved before, so arranging the menu as CEO does not throw away the order
   arranged as Admin. */
export const mergeMenuOrder = (prev, visible) => {
    const keep = (prevKeys, nowKeys) => {
        const all = [...(prevKeys || []), ...nowKeys.filter((k) => !(prevKeys || []).includes(k))];
        return arrange(all, nowKeys);
    };
    const folders = keep(prev && prev.folders, visible.map((f) => f.id));
    const items = { ...((prev && prev.items) || {}) };
    visible.forEach((f) => { items[f.id] = keep(items[f.id], f.items.map(menuKeyOf)); });
    return { folders, items };
};
